<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import lazadaClient from 'dashboard/api/channel/lazadaClient';

// Lazada's authorize page drops its own parameters when a merchant who isn't
// signed in uses "Use Seller Login", ending on "Missing parameter". Signing in
// to Seller Center first avoids that, so the page asks for it as step 1.
// login=1 lands on the login form; without it a signed-out visitor is sent
// to Seller Center's sign-up page.
const sellerCenterLogin = host =>
  `https://${host}/apps/seller/login?login=1&redirect_url=${encodeURIComponent(
    `https://${host}/`
  )}`;

// Which Lazada site each brand's merchants sell on, keyed by account_source.
// Wording and the Seller Center link only: the backend still authorizes
// against Malaysia for every brand.
export const LAZADA_SITES = {
  dahreply: {
    countryKey: 'MALAYSIA',
    sellerCenterUrl: sellerCenterLogin('sellercenter.lazada.com.my'),
  },
  singabot: {
    countryKey: 'SINGAPORE',
    sellerCenterUrl: sellerCenterLogin('sellercenter.lazada.sg'),
  },
};

export default {
  setup() {
    const { accountId } = useAccount();
    return { accountId };
  },

  data() {
    return {
      isConnecting: false,
      hasError: false,
      errorMessage: '',
    };
  },

  computed: {
    ...mapGetters({ getAccount: 'accounts/getAccount' }),
    lazadaSite() {
      const source = this.getAccount(this.accountId)?.custom_attributes
        ?.account_source;
      return LAZADA_SITES[source] || LAZADA_SITES.dahreply;
    },
    country() {
      return this.$t(
        `INBOX_MGMT.ADD.LAZADA.COUNTRIES.${this.lazadaSite.countryKey}`
      );
    },
  },

  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasError = urlParams.get('error');
    const errorMessage = urlParams.get('error_message');
    if (hasError === 'true') {
      this.hasError = true;
      this.errorMessage = errorMessage
        ? decodeURIComponent(errorMessage)
        : this.$t('INBOX_MGMT.ADD.LAZADA.ERROR_MESSAGE');
    }
    if (hasError || errorMessage) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  },

  methods: {
    async connectLazada() {
      this.isConnecting = true;
      this.hasError = false;
      try {
        const response = await lazadaClient.generateAuthorization();
        window.location.href = response.data.auth_url;
      } catch (error) {
        this.hasError = true;
        this.errorMessage =
          error?.response?.data?.message ||
          this.$t('INBOX_MGMT.ADD.LAZADA.ERROR_MESSAGE');
      } finally {
        this.isConnecting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <div class="flex flex-col items-center justify-center h-full">
      <div
        v-if="hasError"
        role="alert"
        class="max-w-2xl p-6 mb-8 text-red-700 bg-red-100 rounded-lg"
      >
        <h3 class="mb-2 text-lg font-semibold">
          {{ $t('INBOX_MGMT.ADD.LAZADA.ERROR_TITLE') }}
        </h3>
        <p class="text-sm">{{ errorMessage }}</p>
      </div>

      <div class="flex flex-col items-center max-w-2xl text-center">
        <div class="mb-8">
          <span
            class="i-ri-shopping-bag-fill text-8xl text-orange-500"
            aria-hidden="true"
          />
        </div>

        <h2 class="mb-4 text-3xl font-bold text-slate-800 dark:text-slate-100">
          {{ $t('INBOX_MGMT.ADD.LAZADA.TITLE') }}
        </h2>

        <p class="mb-8 text-lg text-slate-600 dark:text-slate-300">
          {{ $t('INBOX_MGMT.ADD.LAZADA.SUBTITLE') }}
        </p>

        <ol
          class="flex flex-col w-full gap-4 text-left"
          data-testid="lazada_connect_steps"
        >
          <li
            class="flex gap-4 p-5 border rounded-lg border-slate-100 dark:border-slate-700"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 font-semibold text-white bg-orange-500 rounded-full w-8 h-8"
              aria-hidden="true"
            >
              {{ 1 }}
            </span>
            <div class="flex flex-col items-start gap-2">
              <h3
                class="text-base font-semibold text-slate-800 dark:text-slate-100"
              >
                {{ $t('INBOX_MGMT.ADD.LAZADA.STEP_SIGN_IN.TITLE') }}
              </h3>
              <p class="mb-0 text-sm text-slate-600 dark:text-slate-300">
                {{ $t('INBOX_MGMT.ADD.LAZADA.STEP_SIGN_IN.DESCRIPTION') }}
              </p>
              <a
                :href="lazadaSite.sellerCenterUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-orange-600 border border-orange-500 rounded-full hover:bg-orange-50 dark:hover:bg-orange-900/20"
                data-testid="lazada_open_seller_center"
              >
                {{ $t('INBOX_MGMT.ADD.LAZADA.STEP_SIGN_IN.BUTTON') }}
                <fluent-icon icon="open" size="16" aria-hidden="true" />
              </a>
            </div>
          </li>
          <li
            class="flex gap-4 p-5 border rounded-lg border-slate-100 dark:border-slate-700"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 font-semibold text-white bg-orange-500 rounded-full w-8 h-8"
              aria-hidden="true"
            >
              {{ 2 }}
            </span>
            <div class="flex flex-col items-start gap-2">
              <h3
                class="text-base font-semibold text-slate-800 dark:text-slate-100"
              >
                {{ $t('INBOX_MGMT.ADD.LAZADA.STEP_CONNECT.TITLE') }}
              </h3>
              <p class="mb-0 text-sm text-slate-600 dark:text-slate-300">
                {{
                  $t('INBOX_MGMT.ADD.LAZADA.STEP_CONNECT.DESCRIPTION', {
                    country,
                  })
                }}
              </p>
              <button
                class="flex items-center justify-center px-8 py-3.5 gap-2 text-white rounded-full bg-orange-500 hover:bg-orange-600 hover:shadow-lg transition-all duration-300 min-w-[240px]"
                :disabled="isConnecting"
                :aria-label="
                  isConnecting
                    ? $t('INBOX_MGMT.ADD.LAZADA.CONNECTING_ARIA')
                    : $t('INBOX_MGMT.ADD.LAZADA.CONNECT_BUTTON')
                "
                @click="connectLazada"
              >
                <span
                  class="i-ri-shopping-bag-fill size-5"
                  aria-hidden="true"
                />
                <span class="text-base font-medium">
                  {{
                    isConnecting
                      ? $t('INBOX_MGMT.ADD.LAZADA.CONNECTING')
                      : $t('INBOX_MGMT.ADD.LAZADA.CONNECT_BUTTON')
                  }}
                </span>
                <span v-if="isConnecting" class="ml-2" aria-hidden="true">
                  <svg
                    class="w-5 h-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </li>
        </ol>

        <p class="mt-4 text-sm text-slate-500 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.LAZADA.HELP') }}
        </p>
      </div>
    </div>
  </div>
</template>
